import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openModal } from '../../../redux/reducers/global/modal/modalSlice';
import { PAGE_SIZE_DIMENSIONS, PAGE_SIZE_A4, PAGE_SIZE_ARCH_D } from '../../../utilities/constants/pageSizeConstants';
import { downloadDxf, isDxfDesignNode } from '../../../utilities/export/dxf';
import { registerModalTask } from '../modal/modalTaskRegistry';
import { MODAL_TYPES } from '../modal/modalTypes';

const nodeBounds = (node) => {
  const x = Number(node?.position?.x) || 0;
  const y = Number(node?.position?.y) || 0;
  const width = typeof node?.width === 'number' ? node.width : 100;
  const height = typeof node?.height === 'number' ? node.height : 100;
  return { minX: x, minY: y, maxX: x + width, maxY: y + height };
};

const edgeIsInsidePage = (edge, nodeIds) =>
  nodeIds.has(edge.source) && nodeIds.has(edge.target);

const nodePageKey = (node, pageWidth, pageHeight) => {
  const bounds = nodeBounds(node);
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;
  return {
    ix: Math.floor(centerX / pageWidth),
    iy: Math.floor(centerY / pageHeight),
  };
};

const pageKey = ({ ix, iy }) => `${ix}:${iy}`;

const sortedPagesForNodes = (nodes, pageWidth, pageHeight) => {
  const pageMap = new Map();
  nodes.forEach((node) => {
    const page = nodePageKey(node, pageWidth, pageHeight);
    const key = pageKey(page);
    if (!pageMap.has(key)) pageMap.set(key, page);
  });
  const pages = Array.from(pageMap.values());
  if (pages.length === 0) pages.push({ ix: 0, iy: 0 });
  return pages.sort((a, b) => a.iy - b.iy || a.ix - b.ix);
};

const shiftNodeToPage = (node, x0, y0) => ({
  ...node,
  position: {
    x: (Number(node?.position?.x) || 0) - x0,
    y: (Number(node?.position?.y) || 0) - y0,
  },
});

const shiftEdgeToPage = (edge, x0, y0) => ({
  ...edge,
  data: {
    ...(edge.data || {}),
    waypoints: Array.isArray(edge?.data?.waypoints)
      ? edge.data.waypoints.map((point) => ({
          ...point,
          x: (Number(point?.x) || 0) - x0,
          y: (Number(point?.y) || 0) - y0,
        }))
      : [],
  },
});

const diagramBounds = (nodes) => {
  if (!Array.isArray(nodes) || nodes.length === 0) return null;
  return nodes.reduce((bounds, node) => {
    const current = nodeBounds(node);
    return {
      minX: Math.min(bounds.minX, current.minX),
      minY: Math.min(bounds.minY, current.minY),
      maxX: Math.max(bounds.maxX, current.maxX),
      maxY: Math.max(bounds.maxY, current.maxY),
    };
  }, {
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
  });
};

const shiftNodeBy = (node, dx, dy) => ({
  ...node,
  position: {
    x: (Number(node?.position?.x) || 0) + dx,
    y: (Number(node?.position?.y) || 0) + dy,
  },
});

const shiftEdgeBy = (edge, dx, dy) => ({
  ...edge,
  data: {
    ...(edge.data || {}),
    waypoints: Array.isArray(edge?.data?.waypoints)
      ? edge.data.waypoints.map((point) => ({
          ...point,
          x: (Number(point?.x) || 0) + dx,
          y: (Number(point?.y) || 0) + dy,
        }))
      : [],
  },
});

const centerDiagramOnPage = (nodes, edges, pageWidth, pageHeight) => {
  const bounds = diagramBounds(nodes);
  if (!bounds) return { nodes, edges };
  const diagramWidth = bounds.maxX - bounds.minX;
  const diagramHeight = bounds.maxY - bounds.minY;
  const dx = (pageWidth - diagramWidth) / 2 - bounds.minX;
  const dy = (pageHeight - diagramHeight) / 2 - bounds.minY;
  return {
    nodes: nodes.map((node) => shiftNodeBy(node, dx, dy)),
    edges: edges.map((edge) => shiftEdgeBy(edge, dx, dy)),
  };
};

const sanitizeFilenamePart = (value) =>
  String(value || 'diagram')
    .trim()
    .replace(/[^\w.-]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'diagram';

const ExportToDXFButton = ({ children }) => {
  const dispatch = useDispatch();
  const currentNodes = useSelector((state) => state.node.nodes);
  const currentEdges = useSelector((state) => state.edge.edges);
  const pageSize = useSelector((state) => state.view.pageSize);
  const templateSettings = useSelector((state) => state.view.templateSettings) || {};
  const { width: pageWidth, height: pageHeight } =
    PAGE_SIZE_DIMENSIONS[pageSize] || PAGE_SIZE_DIMENSIONS[PAGE_SIZE_A4];

  const performExport = async (updateProgress, exportOptions = {}) => {
    const includeDesignElements = exportOptions?.includeDesignElements !== false;
    const nodes = Array.isArray(currentNodes) ? currentNodes : [];
    const edges = Array.isArray(currentEdges) ? currentEdges : [];
    if (nodes.length === 0) {
      throw new Error('No hay símbolos para exportar.');
    }

    updateProgress(0);
    const progressInterval = setInterval(
      () => updateProgress((value) => (value < 90 ? value + 10 : value)),
      300
    );

    try {
      const exportableNodes = includeDesignElements
        ? nodes
        : nodes.filter((node) => !isDxfDesignNode(node));
      const pages = sortedPagesForNodes(exportableNodes, pageWidth, pageHeight);
      const selectedPageKey = typeof exportOptions?.selectedPageKey === 'string'
        ? exportOptions.selectedPageKey
        : '';
      const pagesToExport = selectedPageKey
        ? pages
          .map((page, index) => ({ ...page, exportIndex: index }))
          .filter((page) => pageKey(page) === selectedPageKey)
        : pages.map((page, index) => ({ ...page, exportIndex: index }));
      const selectedPages = pagesToExport.length > 0
        ? pagesToExport
        : pages.map((page, index) => ({ ...page, exportIndex: index }));

      const filenameBase = sanitizeFilenamePart(`diagram-${pageSize}`);
      const warnings = [];
      const step = 90 / selectedPages.length;
      let nextProgress = 0;

      for (let idx = 0; idx < selectedPages.length; idx += 1) {
        const { ix, iy, exportIndex } = selectedPages[idx];
        const x0 = ix * pageWidth;
        const y0 = iy * pageHeight;
        const pageNodes = exportableNodes
          .filter((node) => {
            const pageForNode = nodePageKey(node, pageWidth, pageHeight);
            return pageForNode.ix === ix && pageForNode.iy === iy;
          })
          .map((node) => shiftNodeToPage(node, x0, y0));

        const pageNodeIds = new Set(pageNodes.map((node) => node.id));
        const pageEdges = edges
          .filter((edge) => edgeIsInsidePage(edge, pageNodeIds))
          .map((edge) => shiftEdgeToPage(edge, x0, y0));
        const exportPage = includeDesignElements
          ? { nodes: pageNodes, edges: pageEdges }
          : centerDiagramOnPage(pageNodes, pageEdges, pageWidth, pageHeight);

        const pageWarnings = await downloadDxf(
          exportPage.nodes,
          exportPage.edges,
          pageWidth,
          pageHeight,
          `${filenameBase}-p${exportIndex + 1}.dxf`,
          {
            includeDesignElements,
            templateSettings: includeDesignElements
              ? (templateSettings?.pageOption === 'first-page' && exportIndex > 0
                ? { ...templateSettings, pageOption: 'none' }
                : templateSettings)
              : { pageOption: 'none', blocks: [] },
          }
        );
        warnings.push(...pageWarnings);
        nextProgress = Math.min(90, nextProgress + step);
        updateProgress(nextProgress);
      }

      updateProgress(100);
      if (warnings.length > 0) {
        const shown = warnings.slice(0, 5).join('\n');
        const more = warnings.length > 5 ? `\n... y ${warnings.length - 5} mas` : '';
        window.alert(`El DXF se exporto con ${warnings.length} advertencia(s):\n${shown}${more}`);
      }
    } finally {
      clearInterval(progressInterval);
    }
  };

  const handleExportDXF = () => {
    const pageOptions = pageSize === PAGE_SIZE_ARCH_D
      ? sortedPagesForNodes(
        Array.isArray(currentNodes) ? currentNodes : [],
        pageWidth,
        pageHeight,
      ).map((page, index) => ({
        value: pageKey(page),
        label: `Hoja ${index + 1}`,
      }))
      : [];
    const taskId = registerModalTask(async (updateProgress, exportOptions) => {
      await performExport(updateProgress, exportOptions);
    });

    dispatch(
      openModal({
        modalType: MODAL_TYPES.EXPORT_EDITOR_DXF,
        modalProps: {
          pageSize,
          format: 'DXF',
          taskId,
          pageOptions: pageOptions.length > 1 ? pageOptions : [],
        },
      })
    );
  };

  return (
    <div onClick={handleExportDXF}>
      {children || 'Export to DXF'}
    </div>
  );
};

export default ExportToDXFButton;
